import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

interface FinancialPerformanceStatusChartProps {
  data: Array<{
    status: string;
    workshop_count: number;
    avg_estimated_savings: number;
    avg_released_savings: number;
    total_estimated: number;
    total_released: number;
    realization_rate: number;
  }>;
  loading?: boolean;
}

const FinancialPerformanceStatusChart: React.FC<FinancialPerformanceStatusChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Performance by Status</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Performance by Status</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No financial performance data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  const STATUS_COLORS = {
    'new': '#8b5cf6',           // Purple
    'draft': '#6b7280',          // Gray
    'in_progress': '#3b82f6',    // Blue
    'on_hold': '#f59e0b',        // Yellow
    'risk_of_delay': '#f97316',  // Orange
    'closed': '#10b981',         // Green
    'fully_implemented': '#059669', // Dark Green
    'rejected': '#ef4444'        // Red
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Performance by Status</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="status" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatStatus}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                if (name === 'total_estimated') return [formatCurrency(value), 'Total Estimated'];
                if (name === 'total_released') return [formatCurrency(value), 'Total Released'];
                if (name === 'realization_rate') return [`${value.toFixed(1)}%`, 'Realization Rate'];
                return [value, name];
              }}
              labelFormatter={(label) => `Status: ${formatStatus(label)}`}
            />
            <Legend />
            <Bar 
              yAxisId="left"
              dataKey="total_estimated" 
              name="Total Estimated"
              radius={[4, 4, 0, 0]}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-estimated-${index}`} 
                  fill={STATUS_COLORS[entry.status as keyof typeof STATUS_COLORS] || '#6b7280'} 
                />
              ))}
            </Bar>
            <Bar 
              yAxisId="left"
              dataKey="total_released" 
              name="Total Released"
              radius={[4, 4, 0, 0]}
              fill="#10b981"
              opacity={0.7}
            />
            <Bar 
              yAxisId="right"
              dataKey="realization_rate" 
              name="Realization Rate (%)"
              radius={[4, 4, 0, 0]}
              fill="#8b5cf6"
              opacity={0.6}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Status Financial Summary</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.total_estimated - a.total_estimated)
              .map((item) => (
                <div key={item.status} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: STATUS_COLORS[item.status as keyof typeof STATUS_COLORS] || '#6b7280' }}
                    ></div>
                    <span className="text-sm font-medium">{formatStatus(item.status)}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{item.workshop_count}</span> workshops
                    <span className="ml-2 text-xs px-2 py-1 rounded" style={{ 
                      backgroundColor: item.realization_rate >= 50 ? '#10b98120' : '#ef444420', 
                      color: item.realization_rate >= 50 ? '#10b981' : '#ef4444' 
                    }}>
                      {item.realization_rate.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Insights</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Highest Value</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.total_estimated > max.total_estimated ? item : max, data[0])?.status}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {formatCurrency(Math.max(...data.map(d => d.total_estimated)))} estimated
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Realization</span>
                <span className="font-bold text-green-600">
                  {data.reduce((max, item) => item.realization_rate > max.realization_rate ? item : max, data[0])?.status}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...data.map(d => d.realization_rate)).toFixed(1)}% rate
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Workshops</span>
                <span className="font-bold text-gray-900">
                  {data.reduce((sum, item) => sum + item.workshop_count, 0)}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across all statuses
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialPerformanceStatusChart;
